'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMarketplaceStore } from '@/lib/marketplace/store';
import type { MarketplaceReview } from '@/lib/marketplace/types';
import {
  Star,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  MessageSquare,
} from 'lucide-react';

interface ReviewSectionProps {
  listingId: string;
  canReview: boolean;
}

export function ReviewSection({ listingId, canReview }: ReviewSectionProps) {
  const {
    reviews,
    reviewsLoading,
    loadReviews,
    submitReview,
    voteReview,
  } = useMarketplaceStore();

  const [showWriteReview, setShowWriteReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState<'helpful' | 'newest' | 'rating'>('helpful');

  useEffect(() => {
    loadReviews(listingId, { sort_by: sortBy });
  }, [listingId, sortBy, loadReviews]);

  const handleSubmitReview = async () => {
    if (rating === 0) return;

    setSubmitting(true);
    try {
      await submitReview(listingId, {
        rating,
        title: title || undefined,
        body: body || undefined,
      });
      setShowWriteReview(false);
      setRating(5);
      setTitle('');
      setBody('');
    } catch (error) {
      console.error('Failed to submit review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (reviewId: string, isHelpful: boolean) => {
    try {
      await voteReview(reviewId, isHelpful);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const renderStars = (rating: number, interactive = false, onChange?: (r: number) => void) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => onChange?.(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
          >
            <Star
              className={`size-5 ${
                star <= rating
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-muted-foreground'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Reviews</h3>
        <div className="flex items-center gap-4">
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="helpful">Most Helpful</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="rating">Highest Rated</SelectItem>
            </SelectContent>
          </Select>

          {canReview && !showWriteReview && (
            <Button onClick={() => setShowWriteReview(true)}>
              <MessageSquare className="size-4 mr-2" />
              Write Review
            </Button>
          )}
        </div>
      </div>

      {/* Write Review Form */}
      {showWriteReview && (
        <Card>
          <CardHeader>
            <CardTitle>Write a Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="mt-1">
                {renderStars(rating, true, setRating)}
              </div>
            </div>

            <div>
              <Label htmlFor="review-title">Title (optional)</Label>
              <Input
                id="review-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summarize your experience"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="review-body">Review (optional)</Label>
              <Textarea
                id="review-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Share your experience with this item..."
                rows={4}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSubmitReview} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </Button>
              <Button variant="outline" onClick={() => setShowWriteReview(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviewsLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <MessageSquare className="size-12 mx-auto mb-4 opacity-50" />
            <p>No reviews yet</p>
            {canReview && (
              <p className="text-sm">Be the first to review this item!</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      {renderStars(review.rating)}
                      {review.is_verified_purchase && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <CheckCircle2 className="size-3" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    {review.title && (
                      <h4 className="font-medium">{review.title}</h4>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {review.user_name || 'Anonymous'} •{' '}
                    {new Date(review.created_at).toLocaleDateString()}
                  </div>
                </div>

                {review.body && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    {review.body}
                  </p>
                )}

                {/* Helpful Voting */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Was this helpful?
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVote(review.id, true)}
                      className={review.user_voted === true ? 'text-primary' : ''}
                    >
                      <ThumbsUp className="size-4 mr-1" />
                      {review.helpful_count}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleVote(review.id, false)}
                      className={review.user_voted === false ? 'text-primary' : ''}
                    >
                      <ThumbsDown className="size-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
